import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { Observable } from 'rxjs';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';

import { NotificationService } from '../../notification';
import { EnvironmentService } from '../../services/environment.service';
import { HttpErrors } from '../interfaces/http-errors.enum';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  public unauthorizedError$: EventEmitter<void> = new EventEmitter<void>();

  /**
   * This variable defines, if the notification is needed.
   */
  private readonly showNotifications: boolean;

  /**
   * The title of the common error. It stores the translated value.
   */
  private commonErrorTitle: string;

  /**
   * The message of the common error. It stores the translated value.
   */
  private commonErrorMessage: string;

  /**
   * The messages of the errors. It stores the translated values.
   */
  private readonly errors = {
    e500: '',
    e404: '',
    e403: '',
    e401: '',
    noFile: '',
  };

  constructor(
    environmentService: EnvironmentService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService
  ) {
    this.setTranslations();
    this.translate.onLangChange.subscribe(() => this.setTranslations());
    this.showNotifications = environmentService.environment.showNotifications;
  }

  /**
   * This function also can be used by 3rd party plugins, to handle the core's response transformer's errors. Typical .catch() handler.
   * @param errorResponse The Response, that contains the errors.
   * @param caught The error object?
   */
  public errorHandling<T>(errorResponse: HttpErrorResponse, caught?: Observable<T>): Observable<T> {
    if (this.showNotifications) {
      if (errorResponse.error && errorResponse.error.errors && errorResponse.status !== HttpErrors.unauthorized) {
        this.backendErrorHandling(errorResponse);
      } else {
        this.errorStatusHandling<T>(errorResponse, caught);
      }
    }

    return new Observable<T>();
  }

  /**
   *  Sends a common error notification.
   *
   * @memberof ErrorService
   */
  public commonoError() {
    this.notificationService.error(this.commonErrorTitle, this.errors.noFile);
  }

  /**
   * This function populates the error messages and arrays with the translations.
   */
  public setTranslations() {
    this.translate
      .get([
        'errors.common.title',
        'errors.common.message',
        'errors.common.e404',
        'errors.common.e403',
        'errors.common.e401',
        'errors.common.e500',
        'errors.common.no-file',
      ])
      .subscribe((t) => {
        this.commonErrorMessage = t['errors.common.message'];
        this.commonErrorTitle = t['errors.common.title'];
        this.errors.e500 = t['errors.common.e500'];
        this.errors.e404 = t['errors.common.e404'];
        this.errors.e403 = t['errors.common.e403'];
        this.errors.e401 = t['errors.common.e401'];
        this.errors.noFile = t['errors.common.no-file'];
      });
  }

  private backendErrorHandling(errorResponse: HttpErrorResponse) {
    switch (typeof errorResponse.error.errors) {
      case 'object':
        for (const key in errorResponse.error.errors) {
          if (errorResponse.error.errors.hasOwnProperty(key) && errorResponse.error.errors[key] instanceof Array) {
            const errorMessages: string[] = errorResponse.error.errors[key];
            errorMessages.forEach((errorMessage) => this.notificationService.error(key, errorMessage));
          }
        }
        break;
      case 'string':
      default:
        this.notificationService.error(this.commonErrorTitle, errorResponse.error.errors);
    }
  }

  private errorStatusHandling<T>(error: HttpErrorResponse, caught: Observable<T>) {
    switch (error.status) {
      case HttpErrors.internalServerError:
        this.notificationService.error(error.status.toString(), this.errors.e500);
        break;
      case HttpErrors.notFound:
        this.notificationService.error(error.status.toString(), this.errors.e404);
        break;
      case HttpErrors.forbidden:
        this.notificationService.error(error.status.toString(), this.errors.e403);
        break;
      case HttpErrors.unauthorized:
        this.notificationService.error(error.status.toString(), this.errors.e401);
        this.unauthorizedError$.emit();
        break;
      default:
        console.error(error);
        console.error(caught);
        this.notificationService.error(this.commonErrorTitle, this.commonErrorMessage);
    }
  }
}
